import { GENERIC_ERROR_TITLE, toast } from "@/components";
import {
  ApprovalsTab,
  BasicRoutePath,
  DEFAULT_DOMAIN_ID,
  DEFAULT_REQUEST_START_SUB_STEP,
  SECONDARY_REQUEST_START_SUB_STEP,
} from "@/constants";
import { AuthContext } from "@/guards/auth/auth-context";
import { InboxKeyParam } from "@/pages/inbox/types";
import { requestDataTransformer } from "@/pages/requests/transformer";
import { UseHandlersProps } from "@/pages/requests/types";
import {
  selectPersonnelsStore,
  selectRequestFormStore,
  selectUserData,
  submitRequest,
  useBoundStore,
} from "@/services";
import { selectActiveApplicationData } from "@/services/stores/selectors/applications";
import { logger } from "@/utils";
import { useCallback, useContext, useState } from "react";
import {
  useNavigate,
  useParams,
  useSearchParams,
  createSearchParams,
} from "react-router-dom";
import { UsersField } from "../components/users/types";
import { useCheckIfPersonnelsStepExist } from "@/hooks/use-check-if-personnels-step-exist";

export const useHandlers = ({
  onSetStep,
  onSetSubStep,
  formMethods,
  onSetIsValidStep,
  onSetIsLockStepWithLoader,
  step,
  subStep,
  requestDetails,
}: UseHandlersProps) => {
  const [searchParams, setSearchParams] = useSearchParams(); // TODO Need some changes
  const { ReferenceNumber: referenceNumber } = useParams(); // TODO Need some changes
  const navigate = useNavigate();
  const { account } = useContext(AuthContext);

  const { requestFormData } = useBoundStore(selectRequestFormStore);
  const { user } = useBoundStore(selectUserData);
  const { activeApplication } = useBoundStore(selectActiveApplicationData);
  const { selectedPersonnelMode } = useBoundStore(selectPersonnelsStore);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const isPersonelStepExist = useCheckIfPersonnelsStepExist();

  const handleSubmit = useCallback(
    async (isDraft: boolean, isIntermediate?: boolean) => {
      let toastId;

      if (!isIntermediate) {
        toastId = toast.loading("Submitting...");
        setIsSubmitting(true);
      }

      try {
        const controller = new AbortController();

        const data = requestDataTransformer({
          requestFormData,
          selectedPersonnelMode,
          IsAccuracyOfInfoChecked: formMethods.getValues(
            UsersField.UsersAccurancy,
          ),
          activeApplication,
          user,
          referenceNumber:
            referenceNumber ||
            searchParams.get(InboxKeyParam.ReferenceNumber) ||
            "",
          IsDraft: isDraft,
          requestDetails,
        });

        const { ReferenceNumber, RequestId } = await submitRequest(
          {
            email: account?.username ?? "",
            request: data,
          },
          { signal: controller.signal },
        );

        if (!isIntermediate) {
          if (!isDraft) {
            toast.success(
              `Your request has been submitted successfully.\nReference Number: ${RequestId}`,
              {
                id: toastId,
              },
            );
          } else {
            toast.warning(
              "Your request has been saved as a draft successfully.",
              { id: toastId },
            );
          }

          setIsSubmitting(false);

          if (!isDraft && ReferenceNumber) {
            navigate(
              {
                pathname: `${BasicRoutePath.Approvals}/${ApprovalsTab.Tracking}`,
                search: createSearchParams({
                  [InboxKeyParam.ReferenceNumber]: ReferenceNumber,
                  [InboxKeyParam.DomainId]: DEFAULT_DOMAIN_ID,
                }).toString(),
              },
              { replace: true },
            );
          } else {
            navigate(`${BasicRoutePath.Approvals}/${ApprovalsTab.Inbox}`, {
              replace: true,
            });
          }
        } else {
          setSearchParams({
            [InboxKeyParam.ReferenceNumber]: ReferenceNumber,
          });
        }
      } catch (error) {
        logger.log(error);

        toast.error(GENERIC_ERROR_TITLE, { id: toastId });

        setIsSubmitting(false);
      }
    },
    [
      account?.username,
      activeApplication,
      formMethods,
      navigate,
      referenceNumber,
      requestDetails,
      requestFormData,
      searchParams,
      selectedPersonnelMode,
      setSearchParams,
      user,
    ],
  );

  const handleSubmitIntermediateRequest = useCallback(async () => {
    onSetIsLockStepWithLoader(true);

    await handleSubmit(true, true);

    onSetIsLockStepWithLoader(false);
  }, [handleSubmit, onSetIsLockStepWithLoader]);

  const handleNextStep = async () => {
    const isStepValid = await formMethods.trigger();

    if (+step === 1 && !isPersonelStepExist && isStepValid) {
      onSetStep(prevActiveStep => prevActiveStep + 2);
    } else {
      if (
        +step === 2 &&
        subStep === DEFAULT_REQUEST_START_SUB_STEP &&
        requestFormData?.Personnels?.length
      ) {
        onSetSubStep(SECONDARY_REQUEST_START_SUB_STEP);
      } else {
        if (isStepValid) {
          onSetStep(prevActiveStep => prevActiveStep + 1);
        }
      }
    }

    if (+step === 2 && subStep === SECONDARY_REQUEST_START_SUB_STEP) {
      handleSubmitIntermediateRequest();
    }
  };

  const handlePrevStep = () => {
    formMethods.clearErrors();

    if (+step === 3 && subStep === SECONDARY_REQUEST_START_SUB_STEP) {
      // From Contract Location to Personnel
      onSetSubStep(DEFAULT_REQUEST_START_SUB_STEP);
      onSetStep(prevActiveStep => prevActiveStep - 1);
    } else if (+step === 2 && subStep === SECONDARY_REQUEST_START_SUB_STEP) {
      // From Personnel subStep "b" to subStep "a"
      onSetSubStep(DEFAULT_REQUEST_START_SUB_STEP);
    } else if (
      +step === 3 &&
      subStep === DEFAULT_REQUEST_START_SUB_STEP &&
      !isPersonelStepExist
    ) {
      onSetStep(prevActiveStep => prevActiveStep - 2);
    } else {
      // From other steps to Personnel subStep "a"
      onSetStep(prevActiveStep => prevActiveStep - 1);
    }

    // Reset UsersAccurancy
    formMethods.setValue(UsersField.UsersAccurancy, false);

    onSetIsValidStep(true);
  };

  return {
    isSubmitting,
    onSubmit: handleSubmit,
    onNextStep: handleNextStep,
    onPrevStep: handlePrevStep,
  };
};
